import { useEffect, useMemo } from 'react';

import { useFormik } from 'formik';
import styled from '@emotion/styled';
import { toastr } from 'react-redux-toastr';
import { EMAILS_VALIDATION_SCHEMA } from 'pages/signup/validationSchema';
import { mapServerError } from 'core';
import { useStartMinorToAdultMigrationMutation } from 'store/candidate/candidateAPI';

import { Button } from '@britishcouncil/react-solas';
import { Divider, Modal, TextField } from '@britishcouncil/react-solas-ors3';
import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';

import { ServerErrors } from '../ServerErrors';

type MigrateMinorAccountModalFields = {
  email: string;
  confirmEmail: string;
}

export const StartUserMigrationModal = ({
  show,
  onExit,
  candidateId,
}: {
  show: boolean;
  onExit: (isSuccess: boolean) => void;
  candidateId: number;
}) => {
  const [startMinorToAdultMigration, startMinorToAdultMigrationData] = useStartMinorToAdultMigrationMutation();

  const formik = useFormik<MigrateMinorAccountModalFields>({
    enableReinitialize: true,
    validationSchema: EMAILS_VALIDATION_SCHEMA,
    initialValues: {
      email: '',
      confirmEmail: '',
    },
    onSubmit: (values) =>
      startMinorToAdultMigration({
        candidateId,
        emailData: { minorEmail: values.email, confirmMinorEmail: values.confirmEmail },
      }),
  });

  const serverErrors = useMemo(
    () => (startMinorToAdultMigrationData.isError ? mapServerError(startMinorToAdultMigrationData.error) : undefined),
    [startMinorToAdultMigrationData.error, startMinorToAdultMigrationData.isError]
  );

  useEffect(() => {
    if (startMinorToAdultMigrationData.isSuccess) {
      toastr.success('Migration started', 'Minor to adult migration started successfully.');
      onExit(true);
    }
  }, [startMinorToAdultMigrationData.isSuccess, onExit]);

  const mainErrors = useServerErrors(formik, serverErrors);

  const fields = useFormFields(formik);

  return (
    <Modal
      show={show}
      title="Create Account"
      onExit={() => onExit(false)}
      isLoading={startMinorToAdultMigrationData.isLoading}
      footer={
        <Footer>
          <Button intent="primary" onClick={formik.submitForm} disabled={startMinorToAdultMigrationData.isLoading}>
            Confirm
          </Button>
          <Button intent="default" onClick={() => onExit(false)} disabled={startMinorToAdultMigrationData.isLoading}>
            Cancel
          </Button>
        </Footer>
      }
    >
      <Container>
        <p>Create a account for a user who was a minor and now is an adult.</p>
        <Divider></Divider>
        <TextField label="Email" {...fields.email} showColon={false} />
        <TextField label="Confirm email" {...fields.confirmEmail} showColon={false} />
        {!!mainErrors && !startMinorToAdultMigrationData.isLoading && <ServerErrors errors={mainErrors} />}
      </Container>
    </Modal>
  );
};

const Footer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Container = styled.div`
  max-width: 632px;
`;
